import React from 'react';
import { ArrowRight, FolderOpen, FileStack, PenLine, Share2 } from 'lucide-react';

// Combined "What ResiAX helps you do" journey (replaces old ActionCards + 4-step section)
const ACTIONS = [
  {
    icon: FolderOpen,
    step: '1',
    title: 'Capture and organise',
    description: 'Record repairs, complaints, photographs, documents, calls, appointments and notes in one place.',
  },
  {
    icon: FileStack,
    step: '2',
    title: 'Build evidence and continuity',
    description: 'Create a dated history so you don\u2019t have to reconstruct the issue every time someone new becomes involved.',
  },
  {
    icon: PenLine,
    step: '3',
    title: 'Understand and communicate',
    description: 'Use the information you\u2019ve gathered to create clearer reports, updates and letters, with guidance on possible next steps.',
  },
  {
    icon: Share2,
    step: '4',
    title: 'Share and track on your terms',
    description: 'Choose what to share and with whom, then keep a record of responses, appointments, actions and outcomes in your own account.',
  },
];

const JourneySection = () => (
  <section id="how-it-works" className="py-16 lg:py-24 bg-white scroll-mt-20" data-testid="journey-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mb-12">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#B5541B] mb-4">
          <span className="w-8 h-px bg-[#B5541B]" />
          The core journey
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F1E1E] leading-tight mb-4">
          What ResiAX helps you do
        </h2>
        <p className="text-base md:text-lg text-[#3B3535]/70 leading-relaxed">
          From the first report to the latest response, ResiAX helps you keep the issue, evidence
          and communication connected.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-4">
        {ACTIONS.map((action, i) => {
          const Icon = action.icon;
          return (
            <div key={action.title} className="relative group" data-testid={`journey-step-${i}`}>
              <div className="bg-[#FFF8F5] rounded-2xl p-6 h-full border border-transparent hover:border-[#B5541B]/25 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-[#B5541B] flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-widest text-[#B5541B]/70">Step {action.step}</span>
                </div>
                <h3 className="text-lg font-bold text-[#1F1E1E] mb-2">{action.title}</h3>
                <p className="text-sm text-[#3B3535]/70 leading-relaxed">{action.description}</p>
              </div>
              {i < ACTIONS.length - 1 && (
                <div className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-white shadow border border-gray-100 items-center justify-center">
                  <ArrowRight className="w-3.5 h-3.5 text-[#B5541B]" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-8 text-center text-sm font-semibold text-[#3B3535]/60 tracking-wide">
        Capture <span className="text-[#B5541B]">→</span> Build <span className="text-[#B5541B]">→</span> Understand &amp; communicate <span className="text-[#B5541B]">→</span> Share &amp; track
      </p>
    </div>
  </section>
);

export default JourneySection;
